/**
 * Sanitizes database error messages to prevent leaking schema information.
 * Maps known Postgres error codes to user-friendly messages.
 */
export function sanitizeDbError(error: unknown): string {
  if (!error || typeof error !== 'object') {
    return 'An unexpected error occurred. Please try again.';
  }

  const err = error as { code?: string; message?: string };

  if (err.code === 'JOB_VALIDATION_FAILED' || err.message === 'JOB_VALIDATION_FAILED') {
    return 'Please complete the highlighted required job fields before publishing.';
  }
  if (err.code === 'JOB_COMMAND_RESULT_MISMATCH' || err.message === 'JOB_COMMAND_RESULT_MISMATCH') {
    return 'The saved job did not match this form. Your changes were kept; please try again.';
  }
  if (err.code === 'JOB_PERSISTENCE_UNCONFIRMED' || err.message === 'JOB_PERSISTENCE_UNCONFIRMED') {
    return 'Job saving could not be confirmed. Your changes were kept; please try again.';
  }

  // Map known Postgres error codes to safe messages
  switch (err.code) {
    case '23505':
      return 'This item already exists. Please use a different value.';
    case '23503':
      return 'This item is referenced by other data and cannot be modified.';
    case '23502':
      return 'A required field is missing. Please fill in all required fields.';
    case '42501':
      return 'You do not have permission to perform this action.';
    case '42P01':
      return 'An unexpected error occurred. Please try again.';
    case 'PGRST301':
      return 'You do not have permission to perform this action.';
    case 'PGRST116':
      return 'The requested item was not found.';
    default:
      break;
  }

  // Check for common RLS / auth error patterns without exposing details
  if (err.message?.includes('row-level security')) {
    return 'You do not have permission to perform this action.';
  }

  if (err.message?.includes('JWT')) {
    return 'Your session has expired. Please sign in again.';
  }
  if (err.message?.includes('No organization')) {
    return 'Your organization setup is incomplete. Please complete company setup and try again.';
  }

  // Generic fallback — never expose the raw message
  return 'An error occurred. Please try again or contact support.';
}
